import { useCallback, useEffect, useState } from "react";
import { localDateTimeAt } from "../../lib/datetime";
import { useTranslation } from "react-i18next";
import { AlertTriangle, BadgeCheck, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { acknowledgeSalary, myPayrollCycle, reportSalaryNotReceived } from "../../api/payroll";
import { useFeatures } from "../../hooks/useFeatures";
import { ConfirmAckDialog, NotReceivedDialog } from "./SalaryAckDialogs";
import { TrustBadge } from "./TrustBadge";

const GREEN = { background: "linear-gradient(180deg,#22c55e,#15803d)" };
const RED = { background: "linear-gradient(180deg,#ef4444,#b91c1c)" };

// إقرار الموظف: زر أخضر «استلمت» (بتأكيد) وزر أحمر «لم أستلم» (بذكر السبب وإشعار الإدارة).
export const SalaryAckCard = ({ month }) => {
  const { t } = useTranslation();
  const { payroll_cycle: cycleOn } = useFeatures();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    if (!cycleOn) return;
    myPayrollCycle(month).then(setState).catch(() => setState(null));
  }, [month, cycleOn]);

  useEffect(load, [load]);

  if (!cycleOn || !state || state.status === "none") return null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      setState(await fn());
      setModal(null);
      toast.success(t(okMsg));
    } catch {
      toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard data-testid="salary-ack-card">
      <p className="label-xs flex items-center gap-2">
        <HandCoins className="h-4 w-4 text-primary" /> {t("salaryAck.title")}
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        {t(state.status === "paid" ? "salaryAck.paid" : "salaryAck.ready", {
          month,
          amount: `${state.net ?? "—"} ${state.currency || ""}`,
        })}
      </p>
      {state.acknowledged ? (
        <div data-testid="salary-ack-done">
          <p className="mt-3 flex items-center gap-2 text-sm font-bold text-success">
            <BadgeCheck className="h-4 w-4" />
            {t("salaryAck.done", { at: `${localDateTimeAt(state.acknowledged_at, state.acknowledged_tz)}${state.acknowledged_tz_name ? ` · ${state.acknowledged_tz_name}` : ""}` })}
          </p>
          <TrustBadge trust={state.acknowledged_trust} country={state.acknowledged_ip_country} />
        </div>
      ) : state.not_received ? (
        <p className="mt-3 flex items-center gap-2 text-sm font-bold text-destructive" data-testid="salary-ack-denied">
          <AlertTriangle className="h-4 w-4" /> {t("salaryAck.reportedState")}
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <NeuButton variant="primary" style={GREEN} className="flex-1 font-black text-white"
            data-testid="salary-ack-btn" onClick={() => setModal("ack")}>{t("salaryAck.confirm")}</NeuButton>
          <NeuButton variant="primary" style={RED} className="flex-1 font-black text-white"
            data-testid="salary-deny-btn" onClick={() => setModal("deny")}>{t("salaryAck.deny")}</NeuButton>
        </div>
      )}
      {modal === "ack" && (
        <ConfirmAckDialog busy={busy} onNo={() => setModal(null)}
          onYes={() => run(() => acknowledgeSalary(month), "salaryAck.thanks")} />
      )}
      {modal === "deny" && (
        <NotReceivedDialog busy={busy} onClose={() => setModal(null)}
          onSend={(reason) => run(() => reportSalaryNotReceived(month, reason), "salaryAck.reported")} />
      )}
    </NeuCard>
  );
};
